import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { MagnifyingGlass, CaretLeft, CaretRight, Tray } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

export function formatDate(iso, withTime = true) {
  if (!iso) return "-";
  const d = new Date(iso);
  const opts = withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" };
  return d.toLocaleDateString("id-ID", opts);
}

export function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "baru saja";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}j`;
  return `${Math.floor(diff / 86400)}h`;
}

// Nordic flat chips: tinted bg, no border, 4px radius
const STATUS_STYLES = {
  active: "bg-[#0A8A0014] text-[#0A8A00]",
  inactive: "bg-[#1111110A] text-[#767676]",
  setup_required: "bg-[#E8740014] text-[#E87400]",
  DRAFT: "bg-[#1111110A] text-[#484848]",
  PENDING: "bg-[#E8740014] text-[#E87400]",
  APPROVED: "bg-[#0A8A0014] text-[#0A8A00]",
  REJECTED: "bg-[#CC000810] text-[#CC0008]",
  DISABLED: "bg-[#1111110A] text-[#767676]",
  pending: "bg-[#1111110A] text-[#484848]",
  sent: "bg-[#00339912] text-[#003399]",
  delivered: "bg-[#0A8A0014] text-[#0A8A00]",
  read: "bg-[#00339912] text-[#003399]",
  failed: "bg-[#CC000810] text-[#CC0008]",
  opted_in: "bg-[#0A8A0014] text-[#0A8A00]",
  opted_out: "bg-[#CC000810] text-[#CC0008]",
  unknown: "bg-[#1111110A] text-[#767676]",
  revoked: "bg-[#CC000810] text-[#CC0008]",
};

const STATUS_LABELS = {
  active: "Aktif", inactive: "Nonaktif", setup_required: "Setup",
  pending: "Menunggu", sent: "Terkirim", delivered: "Diterima", read: "Dibaca", failed: "Gagal",
  opted_in: "Opt-in", opted_out: "Opt-out", unknown: "Unknown", revoked: "Dicabut",
};

export function StatusBadge({ status, label }) {
  const style = STATUS_STYLES[status] || "bg-[#1111110A] text-[#484848]";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-[4px] text-[11px] font-semibold uppercase tracking-[0.05em] ${style}`} data-testid={`status-${status}`}>
      {label || STATUS_LABELS[status] || status}
    </span>
  );
}

export function BillingBadge({ billing }) {
  const free = billing !== "paid";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase tracking-[0.05em] ${free ? "bg-[#0A8A0014] text-[#0A8A00]" : "bg-[#FFDA1A] text-[#111111]"}`}
      data-testid={`billing-${billing || "free"}`}
    >
      {free ? "Gratis" : "Berbayar"}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
      <div>
        <h1 className="font-heading text-[32px] leading-tight font-bold text-[#111111]">{title}</h1>
        {subtitle && <p className="text-[15px] text-[#484848] mt-1.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ title, description, action, icon: Icon = Tray }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center bg-white rounded-lg" data-testid="empty-state">
      <div className="w-14 h-14 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-5">
        <Icon className="w-7 h-7 text-[#767676]" weight="regular" />
      </div>
      <h3 className="text-xl font-bold text-[#111111] mb-2 font-heading">{title}</h3>
      <p className="text-[15px] text-[#484848] mb-6 max-w-sm leading-relaxed">{description}</p>
      {action}
    </div>
  );
}

export function SearchBar({ value, onChange, placeholder = "Cari...", testid = "search-input" }) {
  return (
    <div className="relative w-full max-w-xs">
      <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#767676]" weight="bold" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9 bg-white h-[42px]" data-testid={testid} />
    </div>
  );
}

// Shared table style tokens (Nordic Yellow) — matches Template page
export const tableClass = "w-full text-sm text-left";
export const theadClass = "text-[11px] uppercase tracking-[0.05em] bg-[#F5F5F5] text-[#484848] font-semibold border-b border-[#DFDFDF]";
export const thClass = "px-6 py-3.5 font-semibold";
// row (clickable) — includes group so first-column can react on hover
export const trClass = "border-b border-[#F5F5F5] last:border-0 hover:bg-[#F5F5F5] transition-colors cursor-pointer group";
export const tdClass = "px-6 py-3.5 text-[#484848]";
// First-column yellow left-border accent that appears on row hover (Nordic Yellow accent)
export const tdFirstClass = "px-6 py-3.5 border-l-2 border-transparent group-hover:border-[#FFDA1A]";


export function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.total_pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-[13px] text-[#484848]">
      <span data-testid="pagination-info">
        {pagination.total_items} total • Halaman {pagination.page} dari {pagination.total_pages}
      </span>
      <div className="flex gap-2">
        <button disabled={!pagination.has_prev} onClick={() => onPage(pagination.page - 1)} className="p-2 rounded-[4px] bg-white disabled:opacity-40 hover:bg-[#ECECEC] transition-colors" data-testid="pagination-prev">
          <CaretLeft className="w-4 h-4" weight="bold" />
        </button>
        <button disabled={!pagination.has_next} onClick={() => onPage(pagination.page + 1)} className="p-2 rounded-[4px] bg-white disabled:opacity-40 hover:bg-[#ECECEC] transition-colors" data-testid="pagination-next">
          <CaretRight className="w-4 h-4" weight="bold" />
        </button>
      </div>
    </div>
  );
}

export function useList(url, extraParams = {}) {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const paramsKey = JSON.stringify({ extraParams, filters });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, q, ...extraParams, ...filters };
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
      const res = await api.get(url, params);
      setData(res.data || []);
      setPagination(res.pagination || null);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [url, page, q, paramsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [q, paramsKey]);

  return { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch: fetchData };
}
